import type { PriceSummary, PriceSummaryReviews } from '../types/priceSummary';

// 가격 화면 칩 건수 요약 읽기(감사 card-controltower#745, 백엔드 PR #93). 모양이 다르면 null — 화면은 두 칩 건수를 비운다.

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
/** 0 이상의 정수만 — 음수 · 소수 · 문자열은 0. */
const count = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** 서버가 주지 않거나 이상한 값이면 이 간격(초)으로 — 백엔드 기본 캐시와 같다. */
export const DEFAULT_PRICE_SUMMARY_SECONDS = 30;
/** 폴링 간격의 하한 · 상한(초) — 서버가 0(캐시 끔) · 아주 큰 값을 줘도 너무 자주 · 너무 드물게 부르지 않는다. */
export const MIN_PRICE_SUMMARY_SECONDS = 10;
export const MAX_PRICE_SUMMARY_SECONDS = 300;

/** price-summary — 칩 두 칸(requests.pending · blocked)이 0 이상의 정수가 아니면 계약이 아니라고 본다. */
export function normalizePriceSummary(raw: unknown): PriceSummary | null {
  const d = obj(raw);
  const rq = obj(d?.requests);
  if (!d || !rq || !isCount(rq.pending) || !isCount(rq.blocked)) return null;
  const rv = obj(d.reviews) ?? {};
  const reviews: PriceSummaryReviews = {
    total: count(rv.total), drop: count(rv.drop), rise: count(rv.rise),
    pending: count(rv.pending), keep: count(rv.keep), apply: count(rv.apply), gatedPending: count(rv.gatedPending),
  };
  const secs = d.cacheSeconds;
  return {
    store:             str(d.store),
    generatedAt:       str(d.generatedAt),
    cacheSeconds:      typeof secs === 'number' && Number.isFinite(secs) && secs > 0 ? secs : DEFAULT_PRICE_SUMMARY_SECONDS,
    // 칸이 없으면(모름) 받았다고 넘겨짚지 않는다 — false 와 같게 건수를 모름으로 둔다.
    pricehubAvailable: d.pricehubAvailable === true,
    pricehubTruncated: d.pricehubTruncated === true,
    requests: {
      total: count(rq.total), available: count(rq.available), pending: rq.pending, blocked: rq.blocked,
      blockedNoCard: count(rq.blockedNoCard),
    },
    reviews,
  };
}

/** 폴링 간격(ms) — 서버 cacheSeconds 를 하한 · 상한 안으로. */
export function priceSummaryPollMs(summary: Pick<PriceSummary, 'cacheSeconds'> | null): number {
  const secs = summary?.cacheSeconds ?? DEFAULT_PRICE_SUMMARY_SECONDS;
  return Math.min(MAX_PRICE_SUMMARY_SECONDS, Math.max(MIN_PRICE_SUMMARY_SECONDS, secs)) * 1000;
}

// ─── 즉시 갱신 신호 ──────────────────────────────────────────────────────────────
// 서버는 이 매장의 매장 가격 요청(apply-current) · 검토 저장(PUT drop-reviews) 뒤 캐시를 바로 비운다 — 화면도 다음 폴링을
// 기다리지 않고 다시 받게 그 두 API 가 끝나면 알린다(성공 · 실패 모두 — 결과 불명이어도 서버 상태가 바뀌었을 수 있다).

type Listener = () => void;
const listeners = new Set<Listener>();

/** 요약을 다시 받아야 할 때(쓰기 뒤) 듣는다. 돌려준 함수로 끊는다. */
export function onPriceSummaryStale(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** 요약이 낡았다고 알린다 — 듣는 쪽이 없으면 아무 일도 없다. */
export function markPriceSummaryStale(): void {
  Array.from(listeners).forEach((l) => {
    try { l(); } catch { /* 듣는 쪽 오류가 쓰기 결과를 바꾸지 않게 */ }
  });
}
